import { useState, useEffect } from 'react'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import PropTypes from 'prop-types'

import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'

const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const weekDays = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']
const nDays = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

const CustomCalender = ({
  disabled,
  activeMonth,
  selectedDays = [],
  previouslySelectedDays = [],
  onSelectDates,
  readOnly,
  disabledDatesAfter,
  disabledDates
}) => {

  const [calenderMatrix, setCalenderMatrix] = useState()
  // dayjs('2019-01-25').daysInMonth() // 31

  useEffect(() => {
    // generate calender on load
    const matrix = generateMatrix(activeMonth)
    setCalenderMatrix(matrix)
    // eslint-disable-next-line
  }, [])

  const generateMatrix = (activeMonth) => {
    let matrix = []

    // Create header
    matrix[0] = weekDays

    const year = activeMonth.getFullYear()
    const month = activeMonth.getMonth()

    const firstDay = new Date(year, month, 1).getDay()
    let maxDays = nDays[month]
    if (month === 1) { // for February
      if ((year % 4 === 0 && year % 100 !== 0) || year % 400 === 0) {
        maxDays += 1
      }
    }

    let counter = 1
    for (let row = 1; row < 7; row++) {
      matrix[row] = []
      for (let col = 0; col < 7; col++) {
        matrix[row][col] = -1
        if (row === 1 && col >= firstDay) { // Fill in rows only after the first day of the month
          matrix[row][col] = counter++
        } else if (row > 1 && counter <= maxDays) { // Fill in rows only if the counter's not greater than the number of days in the month
          matrix[row][col] = counter++
        }
      }
    }
    return matrix
  }


  const onSelectDate = (day) => {
    console.log('[onSelectDate]: ', day)
    onSelectDates && onSelectDates(day)
  }

  return (
    <>
      <Box
        sx={{
          backgroundColor: '#F1F4F7',
          borderRadius: 6,
        }}>

        {/* Calender Title */}
        <Box
          sx={{
            height: 53,
            paddingTop: 2,
            textAlign: 'center',
            backgroundColor: '#E6EBF0',
            borderTopLeftRadius: 5,
            borderTopRightRadius: 5,
          }}>
          <Typography variant="h5"
            sx={{
              color: '#000',
              fontWeight: 600,
            }}>
            {months[activeMonth.getMonth()]}  {activeMonth.getFullYear()}
          </Typography>
        </Box>

        {/* Calender Area */}
        <Box
          sx={{
            paddingBottom: -1,
            '@media (max-width: 600px)': {
              paddingBottom: 10,
            },
          }}>
          {/* calenderMatrix[0] - week day labels, calenderMatrix[1-5] week rows */}
          {calenderMatrix && calenderMatrix.map((row, rowIndex) => {

            const isWeekDayHeader = rowIndex === 0
            const isSecondLastRow = rowIndex === calenderMatrix.length - 2
            // const isLastRow = rowIndex === calenderMatrix.length - 1
            const isEmptyRow = row.filter(value => value !== -1).length === 0

            return isEmptyRow ? null : (
              <Box key={`fbt-calender-row=${rowIndex}`}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-around',
                  paddingBottom: isSecondLastRow ? 0 : 0,
                  ...(isWeekDayHeader && {
                    marginBottom: '-10px',
                  }),
                  '@media (min-width: 990px)': {
                    paddingX: 4,
                  },
                  '@media (min-width: 600px)': {
                    paddingX: 2,
                  },
                }}>
                {row.map((day, index) => {

                  const isValidDay = day !== -1
                  const isDisabledDays = disabledDates && disabledDatesAfter
                    ? day > disabledDatesAfter || disabledDates.includes(day)
                    : disabledDates
                      ? disabledDates.includes(day) : false

                  const isSelected = isValidDay && !isWeekDayHeader && selectedDays.includes(day)
                  const isPreviouslySelectedDays = isValidDay && !isWeekDayHeader && previouslySelectedDays.includes(day)
                  const useGreyCircle = isValidDay && !isDisabledDays && !isWeekDayHeader && !isSelected && !isPreviouslySelectedDays
                  const isSelectable = isPreviouslySelectedDays || useGreyCircle

                  const cellStyle = {
                    flex: 1,
                    height: 50,
                    fontSize: '16px',
                    textAlign: 'center',
                    justifyContent: 'center',
                    paddingTop: '14px',
                    userSelect: 'none',
                    cursor: isSelectable ? 'pointer' : 'default',
                    color: isDisabledDays ? '#DDDDDD' : isSelected ? '#FFF' : '#000',
                    ...(isWeekDayHeader && {
                      color: '#666666 !important',
                      fontSize: '16px',
                    }),
                    backgroundColor: isSelected ? '#00B6B0' : isPreviouslySelectedDays ? '#FFF' : 'transparent',
                    borderRadius: isSelected || isPreviouslySelectedDays ? 50 : 0,
                    borderColor: isPreviouslySelectedDays ? '#DDDDDD' : 'transparent',
                    borderWidth: isPreviouslySelectedDays ? 2 : 0,
                    letterSpacing: isWeekDayHeader ? 1.2 : 'normal',
                    '@media (max-width: 1000px)': {
                      fontSize: '18px',
                      height: 60,
                      paddingTop: '20px',
                      margin: '10px 10px 0 10px',
                    },
                    '@media (max-width: 700px)': {
                      fontSize: '18px',
                      height: 60,
                      paddingTop: '20px',
                      margin: '10px 4px 0 4px',
                    },
                    '@media (max-width: 600px)': {
                      fontSize: '12px',
                      height: 40,
                      paddingTop: '11px',
                      margin: '10px 4px 0 4px',
                    },
                  }

                  return (
                    <Typography
                      key={`custom-calender-day-${index}`}
                      onClick={() => !isValidDay || readOnly || isDisabledDays ? {} : onSelectDate(day)}
                      sx={cellStyle}>
                      {isValidDay && day}
                    </Typography>
                  )
                })}
              </Box>
            )
          })}
        </Box>
      </Box>
      {/* End of Calender Area */}

      {/* Legend Area */}
      <Box
        sx={{
          margin: '10px 0',
        }}>
        <Grid
          style={{
            padding: '2'
          }}>
          {/* <Stack gap='20px' width='50%' textAlign='center'> */}
          <Grid item xs={12} style={{ textAlign: 'center' }}>
            <Typography variant='h5'
              sx={{
                color: '#666666',
                textTransform: 'uppercase',
                letterSpacing: 1.2,
              }}>
              Legend
            </Typography>
            {/* </Stack> */}
          </Grid>
          <Grid item xs={12}
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: 10,
              '@media (max-width: 600px)': {
                display: 'block',
              }
            }}>
            {[
              {
                label: 'Previously Selected',
                color: '#FFF', // white
              }, {
                label: 'Selectable',
                color: '#DDDDDD', // light grey
              }, {
                label: 'Current Selection',
                color: '#00B6B0', // dark blue
              }
            ].map(({ label, color }, legendIndex) => (
              <Box key={`legend-${legendIndex}`} sx={{ display: 'flex' }}>
                <Box
                  sx={{
                    paddingY: 0,
                    paddingX: 1
                  }}>
                  <FiberManualRecordIcon
                    style={{
                      color,
                      width: 15,
                      height: 15,
                      backgroundColor: color,
                      border: `1px solid ${color === '#FFF' ? '#DDDDDD' : color}`,
                      borderRadius: 10,
                    }}/>
                </Box>
                <Typography variant='h6'>
                  {label}
                </Typography>
              </Box>
            ))}
          </Grid>

        </Grid>
      </Box>
      {/* End of Legend Area */}
    </>
  )
}

CustomCalender.propTypes = {
  disabled: PropTypes.bool,
  activeMonth: PropTypes.any,
  selectedDays: PropTypes.array,
  previouslySelectedDays: PropTypes.array,
  readOnly: PropTypes.bool,
  disabledDatesAfter: PropTypes.number,
  disabledDates: PropTypes.array,
  onSelectDates: PropTypes.func.isRequired,
}

export default CustomCalender
